import type { DinComponent } from "./types.ts";
import { DIN_CATALOG } from "./catalog.ts";
import type { PanelRow } from "@electro-planner/domain";

/**
 * Kortkods-tolkning. Matchar vad enlinje.se-videon visar: skriver användaren
 * `DB` fylls en standard-dvärgbrytare i (1-pol B16). Skriver `5G2,5` i
 * kabelkolumnen fylls kabel-attribut in. Alla resultat är *förslag* — UI:t
 * ska visa dem i grått tills användaren accepterar.
 */

/** Resultatet från en kortkod-tolkning. Kan vara null om inget matchar. */
export type ShortCodeSuggestion = {
  /** Fält på `PanelRow` som ska sättas. */
  patch: Partial<PanelRow>;
  /** Mänsklig förklaring för tooltip. */
  explain: string;
};

/**
 * Tolkar en komponent-kortkod (t.ex. "DB16", "JFB3", "JFA20"). Returnerar
 * första match i katalogen.
 */
export function interpretComponentCode(raw: string): ShortCodeSuggestion | null {
  const code = raw.trim().toUpperCase();
  if (code.length === 0) return null;

  // Exakt kortkod-match först.
  for (const c of DIN_CATALOG) {
    if (c.shortCodes.some((sc) => sc.toUpperCase() === code)) {
      return { patch: componentToPatch(c), explain: c.label };
    }
  }

  // Prefix-match: "DB16" → hitta MCB 1P B16A.
  const numeric = code.match(/^([A-ZÅÄÖ]+)(\d+)$/);
  if (numeric) {
    const [, prefix, numStr] = numeric;
    if (prefix && numStr) {
      const amp = parseInt(numStr, 10);
      const hit = DIN_CATALOG.find(
        (c) =>
          c.ampRating === amp &&
          c.shortCodes.some((sc) => sc.toUpperCase().startsWith(prefix)),
      );
      if (hit) return { patch: componentToPatch(hit), explain: hit.label };
    }
  }

  return null;
}

function componentToPatch(c: DinComponent): Partial<PanelRow> {
  const patch: Partial<PanelRow> = { component: c.key };
  if (c.ampRating !== undefined) patch.ampRating = c.ampRating;
  if (c.poles !== undefined) patch.poles = c.poles;
  if (c.characteristic !== undefined) patch.characteristic = c.characteristic;
  if (c.sensitivity !== undefined) patch.sensitivity = c.sensitivity;
  if (c.rcdType !== undefined) patch.rcdType = c.rcdType;
  return patch;
}

/**
 * Tolkar kabelkod som "5G2,5", "3G1.5", "EKK 3G2.5", "N1XV 5G10".
 * Format: [typ] [cores]G[area] — G=gul/grön jordledare, tal skiljer med komma
 * eller punkt.
 */
export function interpretCableCode(raw: string): ShortCodeSuggestion | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const re = /^\s*([A-Za-z0-9]+)?\s*(\d+)\s*[Gg]\s*(\d+[,.]?\d*)\s*$/;
  const m = trimmed.match(re);
  if (!m) return null;
  const [, typeRaw, coresStr, areaStr] = m;
  if (!coresStr || !areaStr) return null;
  const cores = parseInt(coresStr, 10);
  const area = parseFloat(areaStr.replace(",", "."));
  if (!Number.isFinite(cores) || !Number.isFinite(area)) return null;
  const cableType = typeRaw ?? "EKK";
  const patch: Partial<PanelRow> = {
    cableType,
    cableCores: cores,
    cableArea: area,
  };
  const explain = `${cableType} ${cores}G${String(area).replace(".", ",")}`;
  return { patch, explain };
}

/**
 * Slår upp modul-bredd (antal TE) för en rad via dess `component`-nyckel
 * mot DIN-katalogen. Rader utan `component` eller med okänd kortkod antas
 * uppta 1 modul. Används av DIN-layout-visualiseraren för att rita boxar
 * i rätt bredd.
 */
export function getRowModules(row: PanelRow): number {
  if (!row.component) return 1;
  const direct = DIN_CATALOG.find((c) => c.key === row.component);
  if (direct) return direct.modules;
  // Fallback: om raden har en kortkod istället för katalognyckel, matcha
  // via shortCodes.
  const code = row.component.toUpperCase();
  const byShort = DIN_CATALOG.find((c) =>
    c.shortCodes.some((sc) => sc.toUpperCase() === code),
  );
  if (byShort) return byShort.modules;
  return 1;
}

/**
 * Hittar kandidater för autokomplett-lista. Returnerar alla katalog-poster
 * där någon kortkod börjar med användarens input.
 */
export function suggestComponents(prefix: string, limit = 8): DinComponent[] {
  const upper = prefix.trim().toUpperCase();
  if (upper.length === 0) return [];
  const matches: DinComponent[] = [];
  for (const c of DIN_CATALOG) {
    if (c.shortCodes.some((sc) => sc.toUpperCase().startsWith(upper))) {
      matches.push(c);
    }
    if (matches.length >= limit) break;
  }
  return matches;
}
